import React from "react";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";

import PageHeader from "../Common/PageHeader";
import ProfileParametersForm from "./ProfileParametersForm";
import { buildBreadcrumbs } from "helper/breadcrumbs";
import type { UserData, UserCustomer } from "../../types/state";
import { withAuthData, type AuthInjectedProps } from "../../context/AuthContext";

const ProfileParametersFormAny = ProfileParametersForm as unknown as React.ComponentType<
  Record<string, unknown>
>;

interface ProfileParametersPageOwnProps extends WithTranslation {
  userData: UserData;
  customerData?: UserCustomer | Record<string, never>;
  adminToken?: string | boolean | null;
}

type ProfileParametersPageProps = ProfileParametersPageOwnProps & AuthInjectedProps;

interface ProfileParametersFormData {
  allow_email?: boolean | number;
  allow_notification?: boolean | number;
  display_notification?: boolean | number;
  language: string;
  password: string;
  passwordConfirm: string;
}

interface ProfileParametersFormError {
  password?: { code: string; message: string };
}

function isValidPassword(password: string): boolean {
  const password_is_long_enough = password.length >= 8;
  const password_has_uppercase_letter = password.match(/[A-Z]+/) !== null;
  const password_has_digit = password.match(/\d+/) !== null;

  return password_is_long_enough && password_has_uppercase_letter && password_has_digit;
}

function ProfileParametersPage({
  t,
  actions,
  userData,
  customerData,
  adminToken,
  profilePageMessage,
}: ProfileParametersPageProps) {
  const [formData, setFormData] = React.useState<ProfileParametersFormData>({
    allow_email: userData.allow_email,
    allow_notification: userData.allow_notification,
    display_notification: userData.display_notification,
    language: userData.language || "fr",
    password: "",
    passwordConfirm: "",
  });
  const [formError, setFormError] = React.useState<ProfileParametersFormError>({});

  const onFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const field = event.target.name;
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: ProfileParametersFormError = {};

    if (formData.password !== "" || formData.passwordConfirm !== "") {
      if (!isValidPassword(formData.password)) {
        errors.password = {
          code: "PASSWORD_INVALID",
          message: t("profile.parameters.error_submit_min_char"),
        };
      } else if (formData.password !== formData.passwordConfirm) {
        errors.password = {
          code: "PASSWORD_CONFIRM",
          message: t("profile.parameters.error_submit_identical"),
        };
      }
    }

    const isFormContainsError = Object.keys(errors).length > 0;
    const isFormNotEmpty = Object.values(formData).some((e) => e !== "");

    if (isFormContainsError) {
      setFormError(errors);
    } else if (isFormNotEmpty) {
      actions.updateParameters(formData);
      setFormData((prev) => ({ ...prev, password: "", passwordConfirm: "" }));
      setFormError({});
    }
  };

  const breadcrumbs = buildBreadcrumbs(t, "profileParameters");

  return (
    <div className="Page AccountPage">
      <div className="Page-content AccountPage-content">
        <PageHeader
          breadcrumbs={breadcrumbs}
          flash={profilePageMessage}
          title={t("profile.parameters.title")}
          bannerClassName="AccountBanner"
        />
        <ProfileParametersFormAny
          onChange={onFormChange}
          onSubmit={onFormSubmit}
          formData={formData}
          formError={formError}
          userData={userData}
          customerData={customerData}
          message={profilePageMessage}
          readonly={!!adminToken}
        />
      </div>
    </div>
  );
}

export default compose(withTranslation(), withAuthData)(ProfileParametersPage);
